import React from 'react';
import { getTyreLabelLinks, getValueByKey, isTyre, TyreLabels, decodeHtmlEntities } from './utils';
import {
  Accordion,
  AccordionSection,
  Headline,
  Text,
  List,
  TextListItem,
} from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import { SYS_SPACE_FIXED_200, SYS_SPACE_FIXED_300 } from '@oneaudi/unified-web-common';
import {
  AdditionalEquipment as AdditionalEquipmentType,
  CompleteVehicleEntry,
  Features,
  FeaturesText,
  Fod,
  FodInfo,
} from '@oneaudi/vtp-shared';
import { AdditionalProductContainer, TextWrapper, StyledConatiner, StyledTextList } from './styles';
import { TyreDetails } from './TyreDetails';

interface StandardEquipmentTableProps {
  features: Features[];
  vehicleRaw: CompleteVehicleEntry;
  additionalFeatures?: AdditionalEquipmentType[];
  fod?: FodInfo;
  countryCode: string;
}

const createGroupedAdditionalFeaturesList = (features?: AdditionalEquipmentType[]) => {
  if (!features) {
    return undefined;
  }
  const parentGroup = [
    ...new Set(features.map((feature) => feature.groupName && feature.groupName)),
  ];

  if (parentGroup.length < 1) {
    return undefined;
  }

  const result: { [key: string]: AdditionalEquipmentType[] } = {};

  parentGroup.forEach((parent) => {
    if (!parent) {
      return;
    }
    result[parent] = features.filter((feature) => feature.groupName === parent);
  });

  return result;
};

const createGroupedEquipmentList = (features: Features[]) => {
  const parentGroup = [
    ...new Set(features.map((feature) => feature.parentGroupName && feature.parentGroupName)),
  ];

  if (parentGroup.length < 1) {
    return undefined;
  }

  const result: { [key: string]: Features[] } = {};

  parentGroup.forEach((parent) => {
    if (!parent) {
      return;
    }
    result[parent] = features.filter((feature) => feature.parentGroupName === parent);
  });

  return result;
};

export const StandardEquipmentTable: React.FC<StandardEquipmentTableProps> = ({
  features,
  additionalFeatures,
  fod,
  countryCode,
  vehicleRaw,
}) => {
  const groupedEquipmentList = createGroupedEquipmentList(features);
  const tyreLabels: TyreLabels = getTyreLabelLinks(vehicleRaw);

  const additionalInfoHeadline = useI18n({
    id: 'stockcars.equipment.additional.info.headline',
    defaultMessage: '',
  });

  const fodFromText = useI18n({
    id: 'nemo.ui.sc.standards.fods.from',
    defaultMessage: '',
  });
  const fodToText = useI18n({
    id: 'nemo.ui.sc.standards.fods.to',
    defaultMessage: '',
  });
  const fodLifetimeText = useI18n({
    id: 'nemo.ui.sc.standards.fods.lifetimeFeature',
    defaultMessage: '',
  });
  const fodHeadline = useI18n({
    id: 'nemo.ui.sc.standards.fods.headline',
    defaultMessage: '',
  });

  const groupedAddtionalFeaturesList = createGroupedAdditionalFeaturesList(additionalFeatures);
  const fodData = fod?.fods;

  const createListContent = (key: string, text: string, children?: JSX.Element) => {
    return (
      <StyledTextList size="md" variant="bullet" key={key} data-testid="productListItem">
        <TextListItem data-testid="itemInfo">{text}</TextListItem>
        {children && children}
      </StyledTextList>
    );
  };

  const createFoDDurationContent = (fodItem: Fod) => {
    if (fodItem.status !== 'active') {
      return <></>;
    }

    if (fodItem.type === 'FoDRental') {
      return (
        <Text
          variant={{ type: 'copy', order: '3', style: 'normal' }}
          key={`${fodItem.description}-text`}
        >
          {`${fodFromText} `}
          <Text variant={{ type: 'copy', order: '3', style: 'bold' }}>
            {new Date(fodItem.dateStart).toLocaleDateString(countryCode)}
          </Text>
          {` ${fodToText} `}
          <Text variant={{ type: 'copy', order: '3', style: 'bold' }}>
            {new Date(fodItem.dateExpiration).toLocaleDateString(countryCode)}
          </Text>
        </Text>
      );
    }
    if (fodItem.type === 'FoDLifetime') {
      return (
        <Text
          variant={{ type: 'copy', order: '3', style: 'bold' }}
          key={`${fodItem.description}-text`}
        >
          {fodLifetimeText}
        </Text>
      );
    }
    return <></>;
  };

  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const handleToggle = (index: number) => {
    setActiveIndex((prevIndex) => (prevIndex === index ? null : index));
  };
  return (
    <>
      <Headline
        tag="h3"
        variant={{ type: 'headline', order: '4', weight: 'normal' }}
        margin={[0, 0, SYS_SPACE_FIXED_300, 0]}
        data-testid="additionalEquipmentsHeadline"
      >
        {additionalInfoHeadline}
      </Headline>
      {groupedEquipmentList &&
        Object.keys(groupedEquipmentList).map((groupName, index) => (
          <AdditionalProductContainer key={groupName}>
            <Accordion data-testid="AdditonalProductAccordion">
              <AccordionSection
                label={groupName}
                open={activeIndex === index}
                onToggle={() => handleToggle(index)}
              >
                {activeIndex === index && (
                  <List data-testid="StandardEquipmentList">
                    {groupedEquipmentList[groupName]?.map((feature) => {
                      // eslint-disable-next-line no-nested-ternary
                      const featureTexts: FeaturesText[] | undefined = feature.texts
                        ? Array.isArray(feature.texts)
                          ? feature.texts
                          : [feature.texts]
                        : undefined;
                      const headline = decodeHtmlEntities(
                        getValueByKey<FeaturesText>(featureTexts, 'ak_headline')
                      );
                      if (headline.length > 0) {
                        return createListContent(feature.prno, headline);
                      }
                      return null;
                    })}
                  </List>
                )}
              </AccordionSection>
            </Accordion>
          </AdditionalProductContainer>
        ))}
      {groupedAddtionalFeaturesList &&
        Object.keys(groupedAddtionalFeaturesList).map((groupName) => (
          <AdditionalProductContainer key={groupName}>
            <TextWrapper>
              <Text variant={{ type: 'copy', order: '2', style: 'bold' }}>{groupName}</Text>
            </TextWrapper>
            <List>
              {groupedAddtionalFeaturesList[groupName].map((feature) => {
                if (feature.name) {
                  return createListContent(feature.value, feature.name);
                }
                return <></>;
              })}
            </List>
          </AdditionalProductContainer>
        ))}
      {isTyre('RAD') && !!tyreLabels && <TyreDetails tyreLabels={tyreLabels} />}

      {fodData && fodData.length > 0 && (
        <StyledConatiner key={fodHeadline} data-testid="fodInfo">
          <Text variant={{ type: 'copy', order: '1', style: 'normal' }} tag="label">
            {fodHeadline}
          </Text>
          <List margin={[SYS_SPACE_FIXED_200]}>
            {fodData.map((fodItem: any) => {
              const duration = createFoDDurationContent(fodItem);
              return (
                <Text variant={{ type: 'copy', order: '1', style: 'normal' }} data-testid="fodItem">
                  {createListContent(fodItem.description, fodItem.description, duration)}
                </Text>
              );
            })}
          </List>
        </StyledConatiner>
      )}
    </>
  );
};
